import i18n, { languageMap } from 'i18n'
import { resolveTimeFormats } from 'utils/core'

import { setUserEmail } from '../helper/utils'
import { bakePauseGate } from '../helper/bakePauseGate'
import { initMailView } from '../helper/mailview'
import { resolveNineNative } from '../helper/nineNative'
import { createSingleUseFunc } from '../helper/singleUse'
import { blobUrlToDataUrl, isBlobInlineImageSrc } from '../EmailView/copyInlineImages'
import type { PrintPayload } from '../EmailView/printPayload'
import findTextObserver from 'emailThreadViewer/TextFinder/utils/findTextObserver'
import type {
  EmailContainerApi,
  NativeToWebApi,
  WebToNativeApi,
  NineNativeApi,
  ReadReceiptInfo,
  ViewerWindow,
} from 'components/viewer'

// host ↔ viewer 공유 브릿지 계약의 단일 출처는 common-web(components/viewer)이다. 아래 타입들은
// 기존 `emailThreadViewer/ViewerIFace` import 경로를 깨지 않도록 그대로 재노출한다.
export type { ReadReceiptStatus, ReadReceiptInfo, ViewerWindow } from 'components/viewer'

// native host 가 넘기는 locale 을 i18n 리소스 폴더와 일치하는 full code 로 맞춘다.
// owma 뷰어 i18n 은 `load:'currentOnly'` 라 `/locales/{{lng}}/` 를 코드 그대로 fetch 하는데,
// 폴더는 `ko-KR`·`en-US` 형식이라 native 가 2-letter("ko")를 주면 404 → en-US 폴백(메뉴가 영어로
// 고정)된다. owma `localizationUtils.getAvailableLocale` 과 동형으로 languageMap 의 full code 에
// prefix-match 해 "ko"→"ko-KR" 로 정규화한다(웹은 SET_LOCALE saga 가 이 정규화를 거쳐 무해). 매칭이
// 없으면 입력을 그대로 돌려 i18next 자체 fallbackLng 에 맡긴다.
const resolveViewerLocale = (locale: string): string => {
  const match = Object.values(languageMap).find((lang) => lang.code.indexOf(locale) === 0)
  return match?.code ?? locale
}

// `singleUse` 는 helper/singleUse.ts 가 owner — 여기서 augment 하지 않음.
interface IFaceWindow {
  NineNative?: NineNativeApi
  isReactReady?: boolean
  isMailViewReady?: boolean
  webkit?: {
    messageHandlers?: {
      nineHandler?: {
        postMessage: (message: string) => void
      }
    }
  }
  docUtil?: {
    removeViewElement: (emailId: string) => void
  }
  parent?: any
}

declare const window: IFaceWindow & Window

interface TimeFormats {
  dateFormat: string
  timeFormat: string
  dateTimeFormat: string
}

interface Settings {
  dateFormat?: string
  timeFormat?: string
  dateTimeFormat?: string
  defaultBgColor?: string
  useAiSummary?: boolean
  // 본문 mount 방식 — 'shadow-dom' 이면 본문을 shadow root 에 격리한다(EmailView/bodyRoot.ts).
  // 값이 없거나 'light-dom' 이면 기존 light DOM 경로. 값 이름을 theme('light'/'dark')과 섞이지 않게
  // '-dom' 접미사로 명확히 한다. 본문 mount 시점에 읽으므로, 바꾼 값은 viewer remount 뒤에 반영된다.
  bodyIsolation?: 'light-dom' | 'shadow-dom'
  // OMD-243: 메일 본문 iframe 정책(makeMailBodyIframeSanitizer). domainsForIFrame 미지정/빈 배열이면
  // iframe 미관여(web 기본). 지정 시 허용 도메인(https + 서브도메인) 외 iframe 을 제거하고,
  // keepSrcDocForIFrame !== true 면 srcdoc 를 무력화한다. desktop(Tauri)이 OMD-210 때문에 주입한다.
  keepSrcDocForIFrame?: boolean
  domainsForIFrame?: string[]
  [key: string]: any
}

// 뷰어 설정 상태 — host 가 NativeToWeb.setSettings 로 밀어넣고 뷰어만 읽는다(교차 사용 없음).
// (구) 'env' 모듈의 tempEnvParam('settings') 를 대체하는 모듈-로컬 상태 (extract-common-web:
// host 의존 'env' 제거). null 은 "host 가 아직 설정을 밀어넣지 않음"을 뜻한다.
let viewerSettings: Settings | null = null

interface ThreadInfo {
  current?: {
    timer1: NodeJS.Timeout | null
    timer2: number | null
    running: boolean
    clear: () => void
  }
}

interface EmailInfo {
  id: string
  messageId: string
  [key: string]: any
}

interface ThreadData {
  info: any
  emailList: EmailInfo[]
}

const timeFormats: TimeFormats = {
  dateFormat: 'L',
  timeFormat: 'LT',
  dateTimeFormat: 'LLLL',
}

const IFace = {
  // helper/singleUse.ts 가 owner — iframe / non-iframe 양쪽 컨텍스트를 일관되게 처리.
  // 1회용 콜백을 store 에 등록하고 native 가 호출할 식별자를 반환.
  createSingleUseFunc: (cb: (param: any) => void): string => {
    return createSingleUseFunc(cb as (param: unknown) => void)
  },
  isPromise: function (value: unknown): value is Promise<unknown> {
    return value != null && typeof (value as { then?: unknown }).then === 'function'
  },
  nativeCall: function (
    funcName: string,
    returnCallback: ((ret: any) => void) | null,
    paramArray?: any[],
  ): boolean {
    const nine = resolveNineNative()
    if (nine != null) {
      try {
        const fn = nine[funcName]
        if (fn != null) {
          const ret = fn.apply(nine, paramArray ?? [])
          if (returnCallback != null) {
            if (IFace.isPromise(ret)) {
              ret
                .then(function (result: unknown) {
                  returnCallback(result)
                })
                .catch(function (e: unknown) {
                  console.error('nativeCall promise error:', funcName, e)
                })
            } else {
              returnCallback(ret)
            }
          }
          return true
        }
      } catch (e) {
        console.log(e)
      }
    } else if (window.webkit?.messageHandlers?.nineHandler?.postMessage) {
      let returnFuncName = ''
      if (returnCallback != null) {
        returnFuncName = IFace.createSingleUseFunc(returnCallback)
      }
      const param: any = {
        function: funcName,
        returnFunction: returnFuncName,
      }
      if (paramArray != null && paramArray.length > 0) {
        param.params = paramArray
      }
      const paramStr = JSON.stringify(param)
      window.webkit.messageHandlers.nineHandler.postMessage(paramStr)
      return true
    }
    return false
  },
  getTimeFormats: (): TimeFormats => {
    return timeFormats
  },
  getSettings: (): Settings => {
    return {
      ...viewerSettings,
    }
  },
  // 동적 property 할당으로 채워지는 placeholder. initIFace() 안에서 host (viewerExt)
  // 와 viewer 간 bridge 객체 (WebToNative / NativeToWeb) 를 받아 IFace 에 할당. 사용처가
  // `IFace.WebToNative.xxx(...)` 와 `IFace.NativeToWeb.TextFinder.setFindText(...)` 등
  // 함수 + nested 객체 혼재 — 단일 type 으로 좁히면 런타임 호환성을 깬다 (architect
  // 검토 MEDIUM 1-4). 본 트랙에서는 `any` 유지하고 영역별 명세 분리는 별도 트랙.
  WebToNative: {} as WebToNativeApi,
  NativeToWeb: {} as NativeToWebApi,
}

function onReadyIFace(): void {
  if (window.isReactReady === true && window.isMailViewReady === true) {
    IFace.nativeCall('onReady', function (ret) {
      console.log(' -- IFace - onReady - ret : ', ret)
    })
  }
}

export function initIFace(
  viewerWindow: ViewerWindow,
  reactObj: EmailContainerApi,
  isDarkMode: boolean = false,
  darkmodeBackcolor?: string | null,
  globalDarkMode?: boolean,
  darkModeTargetSelector?: string[] | null,
): () => void {
  console.log('initIFace')

  const funcCalledInfo: Record<string, Record<string, number>> = {}
  const funcCalled = (name: string, emailId: string): void => {
    if (funcCalledInfo[name] == null) {
      funcCalledInfo[name] = {}
    }
    if (funcCalledInfo[name][emailId] == null) {
      funcCalledInfo[name][emailId] = 0
    }
    funcCalledInfo[name][emailId] += 1
  }

  const funcReturned = (name: string, emailId: string): void => {
    funcCalledInfo[name][emailId] -= 1
  }

  const getFuncCalledCnt = (name: string, emailId: string): number => {
    return funcCalledInfo[name][emailId]
  }

  const nativeCallWithAsyncCB = (
    funcName: string,
    paramArray: any[],
    useLoadingCnt: boolean,
  ): Promise<any> => {
    const promise = new Promise((resolve) => {
      const returnCallback = (jsonStr: string | null) => {
        // jsonStr === 'null' 인 경우는 null로 callback에 전달됨.
        // jsonStr == null 인 경우는 데이터 전달을 대기하는 상태임.
        if (jsonStr != null) {
          const jsonData = JSON.parse(jsonStr)
          if (useLoadingCnt === true) {
            funcReturned(funcName, paramArray[0])
            resolve({
              data: jsonData,
              remainLoadingCnt: getFuncCalledCnt(funcName, paramArray[0]),
            })
          } else {
            resolve(jsonData)
          }
        }
      }

      const returnFuncName = IFace.createSingleUseFunc(returnCallback)

      if (useLoadingCnt === true) {
        funcCalled(funcName, paramArray[0])
      }
      const params = [...paramArray, returnFuncName]
      IFace.nativeCall(funcName, returnCallback, params)
    })
    return promise
  }

  // OMD-181: 중첩 마운트(InnerViewer 위 EML 미리보기) 대응. 미리보기 EmailContainer 는
  // 호스트와 동일한 전역 IFace 싱글턴을 공유하므로, 덮어쓰기 직전 등록을 스냅샷해 두었다가
  // 이 인스턴스 언마운트 시 {} 초기화가 아니라 직전(호스트) 등록을 복원한다. 토큰 가드로
  // "내가 등록한 값일 때만" 복원해 더 최신 인스턴스의 등록은 건드리지 않는다.
  const prevWebToNative = IFace.WebToNative
  const prevNativeToWeb = IFace.NativeToWeb
  const prevWebObj = viewerWindow.webObj
  const prevIsReactReady = window.isReactReady

  const WebToNative = {
    // OMD-191: 외부 이미지 차단 예외 판별.
    //
    // ⚠️ 반드시 동기(sync)로 동작한다 — async/Promise 금지.
    //   applyBodyElem 의 img 루프(이미지마다 호출되는 hot-path)에서 동기 boolean 이 즉시 필요하다.
    //   그래서 nativeCall(postMessage 콜백 등록/비동기 경로)을 거치지 않고 resolveNineNative() 로
    //   host 함수를 직접 동기 호출한다. host/native 구현체도 반드시 boolean 을 즉시 반환해야 한다.
    //   (Promise 등 non-boolean 을 반환하면 아래 `typeof ret === 'boolean'` 가드에서 무시되어
    //    true 폴백 → 예외가 적용되지 않고 차단된다. 향후 native 구현 시 동기 반환 필수.)
    //
    // 반환: true = 외부 이미지(차단 대상), false = 차단 예외(항상 표시).
    // 브릿지 없음/비동기(postMessage-only native)/throw → true 폴백(보수적 차단, 기존 동작 유지).
    isExternalImageSrc: (src: string): boolean => {
      const nine = resolveNineNative()
      const fn = nine?.isExternalImageSrc
      if (typeof fn === 'function') {
        try {
          const ret = fn.call(nine, src)
          // 동기 boolean 만 신뢰한다. Promise/기타 값은 무시하고 true(차단)로 폴백.
          if (typeof ret === 'boolean') return ret
        } catch (e) {
          console.warn('isExternalImageSrc bridge error:', e)
        }
      }
      return true
    },
    // OMD-193: 복사 시 인라인 이미지의 data URL 변환 대상 스킴 판별. host(desktop)가
    // NineNative.isInlineImageConvertible 을 주입하면 그 판별(officemail-inline:// 등)을 쓰고,
    // 미주입(web)이면 blob: 기본. isExternalImageSrc 와 동일한 sync 직접 호출 패턴.
    isInlineImageConvertible: (src: string): boolean => {
      const nine = resolveNineNative()
      const fn = nine?.isInlineImageConvertible
      if (typeof fn === 'function') {
        try {
          const ret = fn.call(nine, src)
          if (typeof ret === 'boolean') return ret
        } catch (e) {
          console.warn('isInlineImageConvertible bridge error:', e)
        }
      }
      return isBlobInlineImageSrc(src)
    },
    // OMD-193: 인라인 이미지 URL→data URL 변환. host 주입 없으면 web 기본(blobUrlToDataUrl).
    // 선택 시작 시점 선변환(EmailView.ensureInlineDataUrls)에서 호출 — copy 이벤트에서는 동기 소비.
    convertInlineImageToDataUrl: (src: string): Promise<string | null> => {
      const nine = resolveNineNative()
      const fn = nine?.convertInlineImageToDataUrl
      if (typeof fn === 'function') {
        try {
          const ret = fn.call(nine, src)
          if (ret instanceof Promise) return ret
        } catch (e) {
          console.warn('convertInlineImageToDataUrl bridge error:', e)
        }
      }
      return blobUrlToDataUrl(src)
    },
    // OMD-193: host(desktop)가 네이티브 인라인 변환기를 실제로 주입했는지. true 이면
    // isInlineImageConvertible 은 host 소유 커스텀 스킴(officemail-inline://)만 통과시키는 정밀
    // predicate 라, collectConvertibleImgSrcs 가 trackedUrls·마커 게이트를 건너뛰어도 안전하다.
    // (수신 메일 officemail-inline img 는 추적·마커 어느 쪽에도 안 잡혀 게이트에 걸리는 가설③ 대응.)
    // 미주입(web)이면 false → generic blob: 엄격 게이트 유지.
    hasNativeInlineImageConverter: (): boolean => {
      const nine = resolveNineNative()
      return (
        typeof nine?.isInlineImageConvertible === 'function' &&
        typeof nine?.convertInlineImageToDataUrl === 'function'
      )
    },
    checkAllowedAndKnownSender: (emailId: string, senderEmail: string): Promise<any> => {
      console.log(' --- checkAllowedAndKnownSender : ', emailId, senderEmail)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'checkAllowedAndKnownSender',
          (retStr: string) => {
            const ret = JSON.parse(retStr)
            resolve(ret)
          },
          [emailId, senderEmail],
        )
      })
      return promise
    },
    addAllowedSender: (sender: string): Promise<any> => {
      console.log(' --- addAllowedSender : ', sender)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'addAllowedSender',
          (ret: any) => {
            resolve(ret)
          },
          [sender],
        )
      })
      return promise
    },
    addTempAllowedEmailId: (emailId: string): Promise<any> => {
      // 한번 허용한 email을 browser refresh전까지 허용할 수 있도록 설정.
      console.log(' --- addTempAllowedEmailId : ', emailId)
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'addTempAllowedEmailId',
          (ret: any) => {
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    getFileIcon: (filename: string): Promise<any> => {
      return nativeCallWithAsyncCB('getFileIcon', [filename], false)
    },
    getAvatar: (email: string, name: string): Promise<any> => {
      return nativeCallWithAsyncCB('getAvatar', [email, name], false)
    },
    getBody: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('getBody', [emailId], false)
    },
    notifySelect: (emailId: string): void => {
      IFace.nativeCall('notifySelect', null, [emailId])
    },
    setRead: (emailId: string, value: boolean, byUser: boolean): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('setRead', (ret: any) => resolve(ret), [emailId, value, byUser]),
      )
    },
    delete: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('delete', (ret: any) => resolve(ret), [emailId]),
      )
    },
    newMail: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('newMail', (ret: any) => resolve(ret), [emailId]),
      )
    },
    sendAgain: (emailId: string): Promise<any> => {
      return new Promise((resolve) =>
        IFace.nativeCall('sendAgain', (ret: any) => resolve(ret), [emailId]),
      )
    },
    setFlagged: (emailId: string, value: boolean): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'setFlagged',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, value],
        )
      })
      return promise
    },
    editDraft: (emailId: string): void => {
      IFace.nativeCall('editDraft', null, [emailId])
    },
    /**
     * draft-body-style-from-viewer: 뷰어가 draft 본문 표시용 파싱에서 추출한 body 스타일 골격을
     * host 에 보고한다(fire-and-forget). host 는 draft 편집 열기의 defaultStyle 시딩에서 본문
     * 전체 재파싱 대신 이 골격을 쓴다. bodyLen/bodyPrefix 는 stale 검증용(본문 변경 감지).
     * host 미지원(native 앱 등)이면 nativeCall 이 조용히 무시한다.
     */
    reportDraftBodyStyle: (
      emailId: string,
      skeletonHTML: string,
      bodyLen: number,
      bodyPrefix: string,
    ): void => {
      IFace.nativeCall('reportDraftBodyStyle', null, [emailId, skeletonHTML, bodyLen, bodyPrefix])
    },
    /**
     * parsed-body-doc-from-viewer: 본문 표시용 파싱 직후(mutate 전)의 원본 보존 Document 를
     * host 에 전달한다 — host 는 편집(draft)·reply/forward·resend 열기의 본문 재파싱(~4s)
     * 없이 이 Document 를 소비한다.
     *
     * **Gate A(전송 능력)**: Document 는 직렬화 불가라 generic nativeCall(iOS postMessage 경로
     * 포함)을 타지 않고, host 가 same-realm 직접 함수로 핸들러를 노출한 경우에만 clone(makeDoc)
     * 을 생성해 호출한다(fire-and-forget — 미지원 host 는 clone 비용조차 없이 조용히 무시,
     * 기존 전체 파싱 폴백). 캡처 대상 정책(**Gate B**)은 host 가 mailInfo 에 실어 보내는
     * enable_capture_element 가 담당 — 호출부(EmailView)가 검사한다. 두 게이트는 역할이 달라
     * 한쪽을 중복으로 오인해 제거하면 안 된다.
     */
    reportParsedBodyDocument: (
      emailId: string,
      makeDoc: () => Document,
      sourceHTML: string,
    ): void => {
      const nine = resolveNineNative()
      const fn = nine?.reportParsedBodyDocument
      if (typeof fn !== 'function') return
      try {
        fn.call(nine, emailId, makeDoc(), sourceHTML)
      } catch {
        /* additive — 전달 실패는 기존 경로(전체 파싱)로 폴백될 뿐 */
      }
    },
    deleteDraft: (emailId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'deleteDraft',
          (ret: any) => {
            if (ret === true) {
              reactObj.deleteEmail(emailId)
            }
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    openURL: (emailId: string, url: string): void => {
      IFace.nativeCall('openURL', null, [emailId, url])
    },
    showContact: (email: string, name: string): void => {
      IFace.nativeCall('showContact', null, [email, name])
    },
    showFindDialog: (emailId: string): void => {
      IFace.nativeCall('showFindDialog', null, [emailId])
    },
    reply: (emailId: string): void => {
      IFace.nativeCall('reply', null, [emailId])
    },
    replyAll: (emailId: string): void => {
      IFace.nativeCall('replyAll', null, [emailId])
    },
    forward: (emailId: string): void => {
      IFace.nativeCall('forward', null, [emailId])
    },
    forwardAsAttachment: (emailId: string): void => {
      IFace.nativeCall('forwardAsAttachment', null, [emailId])
    },
    getAiSummary: (emailId: string, refresh: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('getAiSummary', [emailId, refresh], true)
    },
    aiSummaryFeedback: (emailId: string, value: string): void => {
      IFace.nativeCall('aiSummaryFeedback', null, [emailId, value])
    },
    getAiExtraction: (emailId: string, refresh: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('getAiExtraction', [emailId, refresh], true)
    },
    aiExtractionFeedback: (emailId: string, value: string): void => {
      IFace.nativeCall('aiExtractionFeedback', null, [emailId, value])
    },
    changeTaskStatus: (emailId: string, taskId: string, taskStatus: string): Promise<any> => {
      return nativeCallWithAsyncCB('changeTaskStatus', [emailId, taskId, taskStatus], false)
    },
    openInNewWindow: (emailId: string): void => {
      IFace.nativeCall('openInNewWindow', null, [emailId])
    },
    viewMessageDetails: (emailId: string): void => {
      IFace.nativeCall('viewMessageDetails', null, [emailId])
    },
    redownloadMessage: (emailId: string): void => {
      IFace.nativeCall('redownloadMessage', null, [emailId])
    },
    responseMeeting: (emailId: string, type: number, meetingResponseAction: number): void => {
      IFace.nativeCall('responseMeeting', null, [emailId, type, meetingResponseAction])
    },
    saveEmail: (emailId: string): void => {
      IFace.nativeCall('saveEmail', null, [emailId])
    },
    // value 는 host 메뉴 액션('other' | 'important')을 그대로 전달한다(host setPriorityInbox 가
    // notifyEmailChanged 의 priority_inbox 로 재전파). boolean 이 아닌 문자열 도메인이다.
    setPriorityInbox: (emailId: string, value: string): void => {
      IFace.nativeCall('setPriorityInbox', null, [emailId, value])
    },
    setThreadImportant: (important: boolean): Promise<any> => {
      return nativeCallWithAsyncCB('setThreadImportant', [important], false)
    },
    onThreadLabelClick: (id: string, name: string): void => {
      IFace.nativeCall('onThreadLabelClick', null, [id, name])
    },
    showInlineImage: (url: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'showInlineImage',
          (ret: any) => {
            resolve(ret)
          },
          [url],
        )
      })
      return promise
    },
    download: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'download',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    cancelDownload: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'cancelDownload',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    downloadAll: (emailId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'downloadAll',
          (ret: any) => {
            resolve(ret)
          },
          [emailId],
        )
      })
      return promise
    },
    // host(NineNative.downloadFile)로 위임. 기존엔 WebToNative 에 래퍼가 없어 EmailView 의
    // 호출이 런타임 undefined 였다(정밀 타이핑으로 드러남) — host 구현에 맞춰 래퍼를 추가한다.
    downloadFile: (emailId: string, attachmentId: string): void => {
      IFace.nativeCall('downloadFile', null, [emailId, attachmentId])
    },
    showPreview: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'showPreview',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    open: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'open',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    saveAs: (emailId: string, attachmentId: string): Promise<any> => {
      const promise = new Promise((resolve) => {
        IFace.nativeCall(
          'saveAs',
          (ret: any) => {
            resolve(ret)
          },
          [emailId, attachmentId],
        )
      })
      return promise
    },
    saveAndOpen: (emailId: string, attachmentId: string): Promise<any> =>
      new Promise((resolve) => {
        IFace.nativeCall('saveAndOpen', (ret: any) => resolve(ret), [emailId, attachmentId])
      }),
    showInFolder: (emailId: string, attachmentId: string): Promise<any> =>
      new Promise((resolve) => {
        IFace.nativeCall('showInFolder', (ret: any) => resolve(ret), [emailId, attachmentId])
      }),

    changeEventStatus: (emailId: string, eventId: string, status: string): Promise<any> => {
      return nativeCallWithAsyncCB('changeEventStatus', [emailId, eventId, status], false)
    },
    addToEvent: (emailId: string, eventId: string): boolean => {
      return IFace.nativeCall('addToEvent', null, [emailId, eventId])
    },
    clearTaskStatus: (emailId: string, taskId: string): Promise<any> => {
      return nativeCallWithAsyncCB('clearTaskStatus', [emailId, taskId], false)
    },

    reservationMailReschedule: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailReschedule', [emailId], false)
    },
    reservationMailCancelSend: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailCancelSend', [emailId], false)
    },
    reservationMailDelete: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('reservationMailDelete', [emailId], false)
    },
    secureSendRenewAccess: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('secureSendRenewAccess', [emailId], false)
    },
    secureSendRemoveAccess: (emailId: string): Promise<any> => {
      return nativeCallWithAsyncCB('secureSendRemoveAccess', [emailId], false)
    },
    requestRefreshReadReceiptInfo: (emailId: string): void => {
      // 호스트(apps/owma) 측에서 캐시 삭제 후 applyReadReceiptInfo 재호출.
      // 응답이 setReadReceiptInfo로 다시 들어오므로 nativeCall fire-and-forget — 의도된 설계
      // (tasks.md §Phase 2.7 / openspec design.md L1149-1156). 응답 채널이 비동기 push이므로
      // 콜백 wait이 불필요. (※ 빈 콜백 자체는 review에서 재지적되지 않도록 명기)
      IFace.nativeCall('requestRefreshReadReceiptInfo', () => {}, [emailId])
    },
    openReadReceiptDetailPopup: (emailId: string): void => {
      // 5차 보완: viewer iframe 내부에서 confirmBase 를 호출하면 마스크가 iframe 영역으로
      // 클립되므로, host 컨텍스트(apps/owma)에서 popup 을 마운트한다.
      // host 가 emailId 로 emailInfoMap.readReceiptInfo.status 를 lookup 후 popup 을 띄운다.
      // fire-and-forget 패턴은 requestRefreshReadReceiptInfo 미러 (tasks.md §Phase 5).
      IFace.nativeCall('openReadReceiptDetailPopup', () => {}, [emailId])
    },
    // OMD-194: 인쇄를 host(apps/owma)에 위임. 뷰어(EmailView)가 payload 를 수집해 전달하고
    // host 의 NineNative.print 가 window.open + 문서 조립 + print() 를 수행한다.
    print: (emailId: string): void => {
      IFace.nativeCall('print', null, [emailId])
    },
  } satisfies WebToNativeApi

  IFace.WebToNative = WebToNative

  const checkTimeFormat = (settings: Settings): void => {
    const resolved = resolveTimeFormats(settings)
    timeFormats.dateFormat = resolved.dateFormat
    timeFormats.timeFormat = resolved.timeFormat
    timeFormats.dateTimeFormat = resolved.dateTimeFormat
  }

  const checkLanguage = (): void => {
    if (viewerSettings != null) {
      checkTimeFormat(viewerSettings)
    }
  }

  i18n.on('languageChanged', checkLanguage)

  const threadInfo: ThreadInfo = {}

  const NativeToWeb = {
    // addEML: (emailId, emlData) => {
    //   reactObj.addEmail(emailId, emlData, 'eml')
    // },
    setUserEmail: (email: string): void => {
      setUserEmail(email)
    },
    setSettings: (settingsStr: string, refresh: boolean): void => {
      const settings = JSON.parse(settingsStr)
      const newSettings = {
        ...viewerSettings,
        ...settings,
      }

      checkTimeFormat(newSettings)

      viewerSettings = newSettings

      if (refresh === true) {
        reactObj.forceUpdate()
      }
    },
    // 뷰어 locale 반영.
    // - IFrameViewer(iframe): 뷰어는 host 와 **별도 realm 의 독립 i18n 인스턴스**를 가지므로 host 의
    //   locale 변경이 전파되지 않는다. 뷰어 자신의 `i18n.changeLanguage` 로 직접 바꿔야 react-i18next
    //   가 languageChanged 로 리렌더하며 번역이 갱신된다.
    // - InnerViewer(host 와 같은 realm, i18n 인스턴스 공유): 이미 host 가 locale 을 바꾼 뒤라
    //   `i18n.language === locale` → changeLanguage 는 no-op 이 되고 forceUpdate 만 수행된다.
    //   (extract-common-web: 'index'(redux store) 의존은 제거된 상태 유지.)
    setLanguage: (locale: string): void => {
      if (locale != null && locale.length > 0) {
        // native 는 "ko" 같은 2-letter 를 넘길 수 있다 → full code("ko-KR")로 정규화 후 비교·반영.
        // 정규화를 먼저 해야 i18n.language 가 이미 "ko-KR" 일 때 "ko" 와 비교해 불필요하게 다시
        // changeLanguage("ko")(→404 폴백) 를 호출하지 않는다.
        const resolved = resolveViewerLocale(locale)
        if (i18n.language !== resolved) {
          void i18n.changeLanguage(resolved)
        }
      }
      reactObj.forceUpdate()
    },
    select: (emailId: string, foldOthers: boolean, byViewer?: boolean): void => {
      reactObj.select(emailId, foldOthers, byViewer)
    },
    /**
     * 검색 결과에서 연 스레드의 매치 메일을 펼친다 — `select` 와 달리 읽음 처리·스크롤·현재 메일
     * 교체가 없다. host 는 `setThread` 의 완료 콜백 뒤에 부른다(그 전엔 `select(…, foldOthers=true)`
     * 가 다시 접는다).
     */
    expandEmails: (emailIds: string[]): number => {
      return reactObj.expandEmails(emailIds)
    },
    setOptimizedBtn: (emailId: string, value: boolean): void => {
      reactObj.setOptimizedBtn(emailId, value)
    },
    setAiExtractionEmail: (aiExtractionEmailId: string): void => {
      console.log(' --- setAiExtractionEmail : ', aiExtractionEmailId)
      reactObj.setAiExtractionEmail(aiExtractionEmailId, true)
    },
    getEmailIds: (): string[] => {
      return reactObj.getEmailIds()
    },
    clearThread: (): void => {
      if (threadInfo.current != null) {
        threadInfo.current.clear()
      }

      reactObj.setLoading(true)

      reactObj.clearAllEmail()

      requestAnimationFrame(() => {
        reactObj.setLoading(false)
      })
    },
    /**
     * `onComplete` — 이 호출의 마지막 단계(`select(defaultEmailId)` 포함)가 끝난 뒤 1회 불린다.
     * 단계 체인은 30ms timer + rAF 로 비동기라 호출 직후엔 선택·접힘이 아직 반영되지 않았고,
     * 그 사이 새 `setThread` 가 오면 `clear()` 가 체인을 끊으므로 **이전 호출의 콜백은 불리지
     * 않는다**(generation-bound). host 가 "선택이 끝난 뒤 다른 메일을 펼치기" 같은 후속 조작의
     * 기준점으로 쓴다. 기존 4-인자 호출(Web·Kotlin)은 그대로 동작한다.
     */
    setThread: (
      threadDataJsonStr: string,
      defaultEmailId: string | null,
      aiExtractionEmailId: string,
      aiExtractionInitialOpen: boolean,
      onComplete?: () => void,
    ): void => {
      if (threadInfo.current != null) {
        threadInfo.current.clear()
      }

      const currentInfo = {
        timer1: null as NodeJS.Timeout | null,
        timer2: null as number | null,
        running: true,
        clear: (): void => {
          currentInfo.running = false
          if (currentInfo.timer1 != null) {
            clearTimeout(currentInfo.timer1)
          }
          if (currentInfo.timer2 != null) {
            cancelAnimationFrame(currentInfo.timer2)
          }
          requestAnimationFrame(() => {
            reactObj.setLoading(false)
          })
        },
      }
      threadInfo.current = currentInfo

      let threadData: ThreadData | EmailInfo[] = JSON.parse(threadDataJsonStr)

      if (Array.isArray(threadData) === true) {
        threadData = {
          info: null,
          emailList: threadData as EmailInfo[],
        }
      }

      const { info, emailList } = threadData as ThreadData

      const funcList = [
        (): void => {
          if (emailList.length === 0) {
            reactObj.setLoading(true)
            reactObj.clearAllEmail(true)
          }
        },
        (): void => {
          // console.log(' --- thread info : ', info, emailList);
          if (emailList.length > 0) {
            reactObj.clearAllEmail(false)
          }

          emailList.forEach((emailInfo: EmailInfo, idx: number) => {
            if (currentInfo.running === true) {
              const isSelected = defaultEmailId == null ? null : emailInfo.id === defaultEmailId
              reactObj.addEmail(emailInfo.id, emailInfo, null, false, isSelected)

              if ((idx + 1) % 5 === 0) {
                setTimeout(() => {
                  reactObj.forceUpdate()
                }, 20)
              }
            }
          })

          reactObj.checkFoldThread()

          reactObj.notifyThreadInfoChanged(info)

          reactObj.setAiExtractionEmail(aiExtractionEmailId, false, aiExtractionInitialOpen)

          reactObj.forceUpdate()
        },
        (): void => {
          if (defaultEmailId != null) {
            reactObj.select(defaultEmailId, true)
          } else {
            reactObj.forceUpdate()
          }
        },
        (): void => {
          reactObj.setLoading(false)
          // 체인이 끊기지 않고 여기까지 온 호출만 완료다(`clear()` 는 running=false 로 체인을
          // 멈추므로 이 함수 자체가 실행되지 않지만, 방어적으로 한 번 더 확인한다).
          if (currentInfo.running === true) {
            onComplete?.()
          }
        },
      ]

      const iter = funcList[Symbol.iterator]()

      const callFunc = (): void => {
        const obj = iter.next()
        if (obj.done === false) {
          obj.value()
          currentInfo.timer1 = setTimeout(() => {
            currentInfo.timer1 = null
            currentInfo.timer2 = requestAnimationFrame(() => {
              currentInfo.timer2 = null
              if (currentInfo.running === true) {
                callFunc()
              }
            })
          }, 30)
        }
      }

      callFunc()
    },
    setHideEmptyIcon: (hideEmptyIcon: boolean): void => {
      reactObj.setHideEmptyIcon(hideEmptyIcon)
    },
    // host 가 탭 전환에 맞춰 뷰어 bake 일시정지 게이트를 토글한다(에디터 탭 활성화=true, 'view'
    // 복귀=false). 뷰어 전역 싱글턴이라 reactObj 를 거치지 않는다. BakePauseController 참고.
    setBakePaused: (paused: boolean): void => {
      if (paused === true) bakePauseGate.pause()
      else bakePauseGate.resume()
    },
    onSetInlineImages: (emailId: string, inlineImagesStr: string): void => {
      const inlineImages = JSON.parse(inlineImagesStr)
      const inlineImagesArr = Array.isArray(inlineImages) === true ? inlineImages : [inlineImages]

      reactObj.setInlineImages(emailId, inlineImagesArr)
    },
    setSecureSendInfo: (emailId: string, dataJsonStr: string): void => {
      const data = JSON.parse(dataJsonStr)
      if (reactObj.setSecureSendInfo != null) {
        reactObj.setSecureSendInfo(emailId, data)
      }
    },
    setReadReceiptInfo: (
      emailId: string,
      isReadReceipt: boolean,
      dataJsonStr: string | null,
    ): void => {
      const data =
        dataJsonStr != null && dataJsonStr.length > 0
          ? (JSON.parse(dataJsonStr) as ReadReceiptInfo)
          : null
      if (reactObj.setReadReceiptInfo != null) {
        reactObj.setReadReceiptInfo(emailId, isReadReceipt, data)
      }
    },
    addEmail: (emailInfoStr: string): void => {
      const emailInfo = JSON.parse(emailInfoStr)
      reactObj.addEmail(emailInfo.id, emailInfo)
    },
    insertEmail: (emailInfoStr: string, afterEmailId: string): void => {
      const emailInfo = JSON.parse(emailInfoStr)
      reactObj.addEmail(emailInfo.id, emailInfo, afterEmailId)
    },
    deleteEmail: (emailId: string): void => {
      reactObj.deleteEmail(emailId)
    },
    notifyEmailChanged: (emailId: string, changedDataStr: string): void => {
      const changedData = JSON.parse(changedDataStr)
      reactObj.notifyEmailChanged(emailId, changedData)
    },
    notifyThreadInfoChanged: (infoJsonStr: string): void => {
      const changedData = JSON.parse(infoJsonStr)
      reactObj.notifyThreadInfoChanged(changedData)
    },
    notifyDownloadProgress: (emailId: string, attachmentId: string, progress: number): void => {
      reactObj.notifyDownloadProgress(emailId, attachmentId, progress)
    },

    /** @deprecated TextFinder.setFindText 사용*/
    setFindText: (findText: string, options?: string): void => {
      reactObj.setFindText(findText, options && JSON.parse(options))
    },

    TextFinder: {
      setFindText: (findText: string, options?: string): void =>
        findTextObserver.notify(findText, options && JSON.parse(options)),
      // TODO
      closeSearchBar: (): null => null,
    },
    // OMD-194: host(NineNative.print)가 인쇄 payload 를 요청하는 진입점.
    // reactObj(EmailContainer)가 emailId 로 EmailView 를 찾아 fold 펼침 후 payload 를 콜백으로 반환.
    requestPrintPayload: (
      emailId: string,
      returnCallback: (payload: PrintPayload | null) => void,
    ): void => {
      reactObj.requestPrintPayload(emailId, returnCallback)
    },
  } satisfies NativeToWebApi

  IFace.NativeToWeb = NativeToWeb

  // jquery / linkify dynamic inject 블록은 §6.3 (linkify-modernization) 으로 제거.
  // helper/linkifyMail 가 모듈 import 로 viewer 번들에 포함되며, EmailView /
  // AiExtraction/Events 가 마운트 시점에서 linkifyMailNode 를 직접 호출.

  // inner mode: EmailContainer 루트 elem 을 DocUtil 생성 시점에 주입(§15.14-⑥ race 회피).
  // 초기 emptyList 면 null → DocUtil 은 doc.body 폴백, 이후 ref 콜백이 setRootContainer 로 재주입.
  initMailView(
    reactObj.getRootContainer?.() ?? null,
    isDarkMode,
    darkmodeBackcolor,
    globalDarkMode,
    darkModeTargetSelector,
  )
  viewerWindow.webObj = IFace.NativeToWeb
  reactObj.extObj = IFace.WebToNative

  window.isReactReady = true
  onReadyIFace()

  // InnerViewer 모드(viewerWindow === host window)에서는 iframe 폐기가 없어, host window 의
  // webObj 가 NativeToWeb 클로저 → reactObj(EmailContainer) 를 영구 보존해 누수가 된다.
  // (iframe 모드는 about:blank 로 contentWindow 가 폐기되며 함께 사라짐.)
  // EmailContainer.componentWillUnmount 에서 호출 — 이 인스턴스가 등록한 bridge 일 때만
  // 정확히 해제한다(다중 마운트 경쟁 시 새 인스턴스 등록을 지우지 않도록 토큰 식별).
  return () => {
    // {} 초기화가 아니라 덮어쓰기 직전(호스트) 등록을 복원한다(OMD-181). webObj 는
    // 이전 값이 없으면 delete, 있으면 복원. isReactReady 도 이전 값으로 되돌려 호스트가
    // 살아 있는데 준비 플래그가 false 로 남는 것을 방지한다.
    if (viewerWindow.webObj === NativeToWeb) {
      if (prevWebObj !== undefined) {
        viewerWindow.webObj = prevWebObj
      } else {
        delete viewerWindow.webObj
      }
    }
    if (IFace.NativeToWeb === NativeToWeb) {
      IFace.NativeToWeb = prevNativeToWeb
    }
    if (IFace.WebToNative === WebToNative) {
      IFace.WebToNative = prevWebToNative
    }
    window.isReactReady = prevIsReactReady ?? false
  }
}

export default IFace
